import { Avatar, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, Grid, IconButton, MenuItem, Select, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { QuickTradeProto, QuoteType, SlTargetValueType as slTargetType, TemplateOrderExecutionParameters } from '../../../../proto/generated/quick-trade'
import { Broker_OrderType, TransactionType } from '../../../../proto/generated/order'
import StrategiesMenu from '../../../predefinedStrategies'
import { ExtraSmallButton, ExtraSmallButtonHoverable, InteractiveTooltip, NonInteractiveTooltip } from '../../../custom'
import { createQuickTrade, deleteQuickTrade, modifyQuickTrade } from '../../../../services/apis/quickTrades'
import SecuritySearchModal from '../../../securitySearch/SecuritySearchModal'
import { LOGOS_BASE_URL } from '../../../../assets/constants'
import { getSecurityAsync, getSecurityKey, getChartTradedSecurity, resolveChartTradedSecurity } from '../../../../util/security'
import { useDispatch } from 'react-redux'
import { getColorForSymbol } from '../../../../util/general'
import { ExpressionCallbackData } from '../../../securitySearch/typings'
import { defaultQuickTrade } from '../../../../util/quicktradewidget'
import DraggableDialogTitle from '../../DraggableDialogTitle'
import { ClearIcon } from '@mui/x-date-pickers'
import SlTargetClearIcon from '../../../../assets/icons/ClearIcon'
import { setOpenQuickTradeSettingsDialog } from '../../../../reducer/quickTrades.reducer'
import websocketActions from '../../../../actions/websocket.actions'
import { getOrderBook } from '../../../../services/apis/candles'
import { updateOrderBooks } from '../../../../reducer/security.reducer'
import { useSelector } from 'react-redux'
import { selectOrderBook } from '../../../../selectors/security'
import { selectQuickTrade, selectQuickTradeDialogMode, selectQuickTradeDialogOpen, selectQuickTradeEditTemplateId, selectQuickTrades } from '../../../../selectors/quickTrades'
import BinIcon from '../../../../assets/icons/BinIcon'
import Edit from '../../../../assets/icons/drawingTools/edit'
import ArrowBackIcon from '../../../../assets/icons/ArrowBackIcon'
import AddIcon from '../../../../assets/icons/AddIcon'
import { StrategyTemplateLegProto, StrategyTemplateProto } from '../../../../proto/generated/strategy'
import StrategyLegsRenderer from '../../../strategyTemplate/StrategyLegsRenderer'
import { Security, SecurityCategory_CategoryCode } from '../../../../proto/generated/security'
import { selectPrimarySecurity } from '../../../../selectors/home'
import { Exchange } from '../../../../proto/generated/common'
import Info from '../../../../assets/icons/InfoIcon'
import QuickTradeTooltip from './QuickTradeTooltip'
import QuickTradeConditionalEntryWithLiveQuote from '../../../quickTradeWindow/QuickTradeConditionalEntryWithLiveQuote'
import QuickTradeQuantityFieldWithLiveQuote from './QuickTradeQuantityFieldWithLiveQuote'
import QuickTradePriceOffsetFieldWithLiveQuote from './QuickTradePriceOffsetFieldWithLiveQuote'
import { slTargetClearBtnSx } from './quickTradeFieldChrome'
import { isFoSecurity, parseTemplateQuantityInput, validateTemplateQuantityForSave } from '../../../../util/quickTradeQuantity'
import { computeQuickTradeEntryPriceDisplay, readQuickTradeQuoteScalars } from '../../../../util/quickTradeQuote'
import { QuickTradeQuantityType } from './QuickTradeQuantityField'
import {
    buildStrategySpreadConditionalSnapshot,
    ConditionalEntryState,
    anyConditionalRuleRequiresPinnedSymbol,
    hasConditionalRuleContent,
    isConditionalEntryReady,
} from '../../../../util/conditionalEntry'
import {
    conditionalEntryStateFromTemplate,
    conditionalExitStateFromTemplate,
} from '../../../../util/quickTradeTemplateConditional'
import { parseStrategyTemplateOrders } from '../../../strategyTemplate/StrategyParser'
import { useUser } from '../../../../context/UserContext'
import { FeatureCategory, FeatureName } from '../../../adminUserRestrictions/Restrictions.constants'
import { TradeOrder } from '../../../../reducer/strategyBuilder.reducer'

type Props = {}

const QUANTITY_MULTIPLIER = 'Multiplier' as const
type TemplateQuantityType = QuickTradeQuantityType | typeof QUANTITY_MULTIPLIER

const aboveFieldLabelSx = { color: 'text.secondary', textTransform: 'uppercase' } as const
const aboveFieldColumnSx = { gap: 0.5, width: '100%' } as const

const chartPrimarySecurity = (primary?: { code?: string; exchange?: number }) =>
    primary?.code ? { code: primary.code, exchange: primary.exchange ?? Exchange.NSE } : undefined

const hasActiveConditional = (entry: ConditionalEntryState, exit: ConditionalEntryState) =>
    entry.enabled ||
    exit.enabled ||
    hasConditionalRuleContent(entry.condition) ||
    hasConditionalRuleContent(exit.condition)

const AddQuickTrade = (props: Props) => {
    const dispatch = useDispatch()
    const theme = useTheme()
    const primarySecurity = useSelector(selectPrimarySecurity)
    const [chartSecurity, setChartSecurity] = useState<Security>()
    useEffect(() => {
        let cancelled = false
        resolveChartTradedSecurity(primarySecurity).then((sec) => {
            if (!cancelled) setChartSecurity(sec)
        })
        return () => {
            cancelled = true
        }
    }, [primarySecurity])

    const [securityAuto, setSecurityAuto] = useState(true)
    const [quickTrade, setQuickTrade] = useState<QuickTradeProto>(defaultQuickTrade())
    const [selectedSecurity, setSelectedSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    const securityKey = useMemo(
        () => (quickTrade.security ? getSecurityKey(quickTrade.security?.exchange ?? 0, quickTrade.security?.code ?? '') : ''),
        [quickTrade.security?.code, quickTrade.security?.exchange]
    )
    useEffect(() => {
        if (securityKey) getSecurityAsync(securityKey).then(setSelectedSecurity)
        else setSelectedSecurity(undefined)
    }, [securityKey])

    const quantitySecurity = securityAuto
        ? chartSecurity ?? getChartTradedSecurity(primarySecurity)
        : selectedSecurity ?? chartSecurity
    const foQuantityMode = isFoSecurity(quantitySecurity)

    const open = useSelector(selectQuickTradeDialogOpen)
    const editTemplateId = useSelector(selectQuickTradeEditTemplateId)
    const quickTradeDialogMode = useSelector(selectQuickTradeDialogMode)
    const [quantityType, setQuantityType] = useState<TemplateQuantityType>(QuickTradeQuantityType.QUANTITY)
    const quickTrades = useSelector(selectQuickTrades)
    const editQuickTrade = useSelector(selectQuickTrade(editTemplateId ?? ''))
    const [selectedTradeType, setSelectedTradeType] = useState<'buy' | 'sell' | 'strategy'>('strategy')
    const [nameError, setNameError] = useState('')
    const [templateError, setTemplateError] = useState('')
    const [quantityError, setQuantityError] = useState('')
    const [mode, setMode] = useState<'add' | 'edit' | undefined>('add')
    const [addStopLoss, setAddStopLoss] = useState(false)
    const [addTarget, setAddTarget] = useState(false)
    const [stopLoss, setStopLoss] = useState<number | ''>('')
    const [target, setTarget] = useState<number | ''>('')
    const [stopLossType, setStopLossType] = useState<slTargetType>(slTargetType.POINTS)
    const [targetType, setTargetType] = useState<slTargetType>(slTargetType.POINTS)
    const [conditionalEntry, setConditionalEntry] = useState<ConditionalEntryState>({ enabled: false })
    const [conditionalExit, setConditionalExit] = useState<ConditionalEntryState>({ enabled: false })
    const [strategyTradeOrders, setStrategyTradeOrders] = useState<TradeOrder[]>([])

    const resetConditionalState = useCallback(() => {
        setConditionalEntry({ enabled: false })
        setConditionalExit({ enabled: false })
    }, [])

    const resetSlTargetOnly = useCallback(() => {
        setAddStopLoss(false)
        setAddTarget(false)
        setStopLoss('')
        setTarget('')
        setStopLossType(slTargetType.POINTS)
        setTargetType(slTargetType.POINTS)
    }, [])

    const resetSlTargetState = useCallback(() => {
        resetSlTargetOnly()
        resetConditionalState()
    }, [resetSlTargetOnly, resetConditionalState])

    const isStrategyMode = useMemo(()=> selectedTradeType === 'strategy', [selectedTradeType])

    const showQuantity = !isStrategyMode && !foQuantityMode

    const hydrateSlTargetFromTemplate = useCallback((params: QuickTradeProto['templateOrderExecutionParameters']) => {
        if (!params?.addStopLoss && !params?.addTarget && params?.stopLoss == null && params?.target == null) {
            resetSlTargetOnly()
            return
        }
        setStopLoss(params.stopLoss ?? '')
        setTarget(params.target ?? '')
        setAddStopLoss(params.stopLoss != null)
        setAddTarget(params.target != null)
        setStopLossType(params.stopLossType ?? slTargetType.POINTS)
        setTargetType(params.targetType ?? slTargetType.POINTS)
    }, [resetSlTargetOnly])

    const applySlTargetToTemplateParams = useCallback((
        params: TemplateOrderExecutionParameters
    ) => {
        const next = { ...params }
        if (addStopLoss && stopLoss !== '') {
            next.addStopLoss = true
            next.stopLoss = Number(stopLoss)
            next.stopLossType = isStrategyMode
                ? slTargetType.MTM
                : stopLossType
        } else {
            next.addStopLoss = undefined
            next.stopLoss = undefined
            next.stopLossType = undefined
        }
        if (addTarget && target !== '') {
            next.addTarget = true
            next.target = Number(target)
            next.targetType = isStrategyMode
                ? slTargetType.MTM
                : targetType
        } else {
            next.addTarget = undefined
            next.target = undefined
            next.targetType = undefined
        }
        return next
    }, [addStopLoss, stopLoss, stopLossType, addTarget, target, targetType, isStrategyMode])

    const hydrateConditionalFromTemplate = useCallback((params: QuickTradeProto['templateOrderExecutionParameters']) => {
        if (!params) {
            resetConditionalState()
            return
        }
        const entry = conditionalEntryStateFromTemplate(params)
        const exit = conditionalExitStateFromTemplate(params)
        if (!entry.enabled && !exit.enabled) {
            resetConditionalState()
            return
        }
        setConditionalEntry({
            enabled: entry.enabled,
            condition: entry.condition ? structuredClone(entry.condition) : undefined,
        })
        setConditionalExit({
            enabled: exit.enabled,
            condition: exit.condition ? structuredClone(exit.condition) : undefined,
        })
    }, [resetConditionalState])

    const applyConditionalToTemplateParams = useCallback((
        params: TemplateOrderExecutionParameters
    ) => {
        const next = { ...params }
        if (conditionalEntry.enabled && hasConditionalRuleContent(conditionalEntry.condition)) {
            next.addConditionalEntry = true
            next.conditionalEntryCondition = structuredClone(conditionalEntry.condition!)
        } else {
            next.addConditionalEntry = undefined
            next.conditionalEntryCondition = undefined
        }
        if (conditionalExit.enabled && hasConditionalRuleContent(conditionalExit.condition)) {
            next.addConditionalExit = true
            next.conditionalExitCondition = structuredClone(conditionalExit.condition!)
        } else {
            next.addConditionalExit = undefined
            next.conditionalExitCondition = undefined
        }
        return next
    }, [conditionalEntry, conditionalExit])

    const applyTemplateParamsExtras = useCallback((
        params: TemplateOrderExecutionParameters
    ) => applyConditionalToTemplateParams(applySlTargetToTemplateParams(params)), [applySlTargetToTemplateParams, applyConditionalToTemplateParams])

    const applySymbolModeForConditionals = useCallback(
        (entry: ConditionalEntryState, exit: ConditionalEntryState) => {
            if (!hasActiveConditional(entry, exit)) return
            const needsPin = anyConditionalRuleRequiresPinnedSymbol(entry.condition, exit.condition)
            if (needsPin) {
                setSecurityAuto(false)
                setQuickTrade((q) =>
                    q.security?.code ? q : { ...q, security: chartPrimarySecurity(quantitySecurity) ?? q.security }
                )
            } else {
                setSecurityAuto(true)
                setQuickTrade((q) => ({ ...q, security: undefined }))
            }
        },
        [quantitySecurity]
    )

    const handleConditionalEntryChange = useCallback(
        (next: ConditionalEntryState) => {
            setConditionalEntry(next)
            applySymbolModeForConditionals(next, conditionalExit)
        },
        [conditionalExit, applySymbolModeForConditionals]
    )

    const handleConditionalExitChange = useCallback(
        (next: ConditionalEntryState) => {
            setConditionalExit(next)
            applySymbolModeForConditionals(conditionalEntry, next)
        },
        [conditionalEntry, applySymbolModeForConditionals]
    )

    const requiresPinnedSymbol = useMemo(
        () => anyConditionalRuleRequiresPinnedSymbol(conditionalEntry.condition, conditionalExit.condition),
        [conditionalEntry.condition, conditionalExit.condition]
    )

    useEffect(() => {
        if (quickTradeDialogMode === 'view') {
            setMode(undefined)
            setQuickTrade(defaultQuickTrade())
            setSecurityAuto(true)
        } else {
            if (editTemplateId) {
                const newTrade = editQuickTrade ? { ...editQuickTrade } : defaultQuickTrade()
                const params = editQuickTrade?.templateOrderExecutionParameters
                const hasConditional =
                    !!params?.addConditionalEntry ||
                    !!params?.addConditionalExit ||
                    hasConditionalRuleContent(params?.conditionalEntryCondition) ||
                    hasConditionalRuleContent(params?.conditionalExitCondition)
                const needsPin = anyConditionalRuleRequiresPinnedSymbol(
                    params?.conditionalEntryCondition,
                    params?.conditionalExitCondition
                )
                if (needsPin && !newTrade.security?.code) {
                    newTrade.security = chartPrimarySecurity(quantitySecurity)
                }
                const hydratedParams = newTrade.templateOrderExecutionParameters
                if (
                    hydratedParams?.quoteType === QuoteType.LEVEL &&
                    hydratedParams.quoteValue == null
                ) {
                    newTrade.templateOrderExecutionParameters = {
                        ...hydratedParams,
                        quoteValue: 0,
                    }
                }
                setQuickTrade(newTrade)
                setSecurityAuto(hasConditional ? !needsPin : !editQuickTrade?.security?.code)
                const nextTypeForBuySell = foQuantityMode ? QuickTradeQuantityType.LOTS : QuickTradeQuantityType.QUANTITY
                setQuantityType(
                    params?.amount != null
                        ? QuickTradeQuantityType.AMOUNT
                        : params?.quantity != null
                          ? QuickTradeQuantityType.QUANTITY
                          : params?.lots != null
                            ? QuickTradeQuantityType.LOTS
                            : nextTypeForBuySell
                )
                setSelectedTradeType(editQuickTrade && editQuickTrade.templateOrderExecutionParameters?.transactionType ? (editQuickTrade.templateOrderExecutionParameters.transactionType === TransactionType.BUY ? 'buy' : 'sell') : 'strategy')
                setMode('edit')
                hydrateSlTargetFromTemplate(editQuickTrade?.templateOrderExecutionParameters)
                hydrateConditionalFromTemplate(editQuickTrade?.templateOrderExecutionParameters)
            } else {
                setQuickTrade(defaultQuickTrade())
                setSecurityAuto(true)
                setMode('add')
                resetConditionalState()
            }
        }
    }, [editTemplateId, editQuickTrade, chartSecurity, quickTradeDialogMode, hydrateSlTargetFromTemplate, hydrateConditionalFromTemplate, resetConditionalState])

    const [securityModalOpen, setSecuritiesModalOpen] = useState<boolean>(false)

    useEffect(() => {
        if (!isStrategyMode || !selectedSecurity?.code) {
            setStrategyTradeOrders([])
            return
        }
        const legs = quickTrade.templateOrderExecutionParameters?.strategyLegs
        if (!legs?.strategyTemplateLegs?.length) {
            setStrategyTradeOrders([])
            return
        }
        let cancelled = false
        parseStrategyTemplateOrders(
            { strategyTemplateLegs: legs, name: '' } as StrategyTemplateProto,
            selectedSecurity
        ).then((orders) => {
            if (!cancelled) setStrategyTradeOrders(orders)
        })
        return () => {
            cancelled = true
        }
    }, [isStrategyMode, selectedSecurity, quickTrade.templateOrderExecutionParameters?.strategyLegs])

    const quantityQuoteKey = useMemo(() => {
        if (!securityAuto && securityKey) return securityKey
        if (quantitySecurity?.code) {
            return getSecurityKey(quantitySecurity.exchange ?? Exchange.NSE, quantitySecurity.code)
        }
        return ''
    }, [securityAuto, securityKey, quantitySecurity?.code, quantitySecurity?.exchange])

    const quantityTransactionType =
        selectedTradeType === 'sell' ? TransactionType.SELL : TransactionType.BUY

    const conditionalDisplaySymbol = quantitySecurity?.symbol ?? quantitySecurity?.code ?? ''

    const needOrderBook =   // for level price offset
        open &&
        quickTrade.templateOrderExecutionParameters?.brokerOrderType === Broker_OrderType.LIMIT_ORDER &&
        quickTrade.templateOrderExecutionParameters?.quoteType === QuoteType.LEVEL
    const levelOrderBook = useSelector(selectOrderBook(quantityQuoteKey))

    useEffect(() => {
        if (!needOrderBook || levelOrderBook || !quantitySecurity?.code) return
        let cancelled = false
        getOrderBook({ security: quantitySecurity, dispatch })
            .then((result) => {
                if (cancelled) return
                const orderBook = { ...result }
                if (!orderBook.security) {
                    orderBook.security = { ...quantitySecurity }
                }
                dispatch(updateOrderBooks([orderBook]))
            })
            .catch((error) => {
                console.error('Error fetching order book:', error)
            })
        return () => {
            cancelled = true
        }
    }, [needOrderBook, levelOrderBook, quantitySecurity, dispatch])

    useEffect(() => {
        if (!open || !quantitySecurity?.code) return
        websocketActions.setWebsocketSubscribeList(dispatch, {
            orderBookSubscriptions: [
                {
                    tag: 'addQuickTradeOrderBook',
                    securities: [{ exchange: quantitySecurity.exchange, code: quantitySecurity.code }],
                    topBidAskOnly: false,
                },
            ],
        })
        return () => {
            websocketActions.setWebsocketSubscribeList(dispatch, {
                orderBookSubscriptions: [
                    {
                        tag: 'addQuickTradeOrderBook',
                        securities: [],
                        topBidAskOnly: false,
                    },
                ],
            })
        }
    }, [open, quantitySecurity?.code, quantitySecurity?.exchange, dispatch])

    const quantityFieldValue = useMemo(() => {
        const params = quickTrade.templateOrderExecutionParameters
        if (quantityType === QuickTradeQuantityType.AMOUNT) return params?.amount
        if (quantityType === QuickTradeQuantityType.LOTS) return params?.lots
        if (quantityType === QuickTradeQuantityType.QUANTITY) return params?.quantity
        return params?.multiplier
    }, [quickTrade.templateOrderExecutionParameters, quantityType])

    const strategySpreadConditionalSnapshot = useMemo(
        () => buildStrategySpreadConditionalSnapshot(strategyTradeOrders, undefined),
        [strategyTradeOrders]
    )

    const basketConditionalModalVariants = useMemo(
        () =>
            isStrategyMode && strategySpreadConditionalSnapshot
                ? { spreadSnapshot: strategySpreadConditionalSnapshot, initialRef: 'spread' as const }
                : undefined,
        [isStrategyMode, strategySpreadConditionalSnapshot]
    )

    const showAmount =
        selectedTradeType === 'buy' ||
        (quickTrade?.templateOrderExecutionParameters?.strategyLegs?.strategyTemplateLegs?.length === 1 &&
            quickTrade?.templateOrderExecutionParameters?.strategyLegs?.strategyTemplateLegs?.[0].transactionType ===
                TransactionType.BUY)

    useEffect(() => {
        if (isStrategyMode) {
            if (quantityType === QuickTradeQuantityType.AMOUNT) {
                handleMultiplierChange({ target: { value: 10000 } } as any, QuickTradeQuantityType.AMOUNT)
            } else {
                setQuantityType(QUANTITY_MULTIPLIER)
                handleMultiplierChange({ target: { value: 1 } } as any, QUANTITY_MULTIPLIER)
            }
            return
        }
        if (quantityType === QuickTradeQuantityType.AMOUNT && showAmount) return

        const nextType = foQuantityMode ? QuickTradeQuantityType.LOTS : QuickTradeQuantityType.QUANTITY
        if (quantityType !== nextType || String(quantityType) === QUANTITY_MULTIPLIER) {
            setQuantityType(nextType)
            handleMultiplierChange({ target: { value: 1 } } as any, nextType)
        }
    }, [isStrategyMode, selectedTradeType, showAmount, foQuantityMode, quantitySecurity?.code, quantitySecurity?.lotSize])

    const showTradeOptions = useMemo(() => {
        if (!selectedSecurity || securityAuto) return ['strategy', 'buy', 'sell']
        if (selectedSecurity?.hasFo) {
            if (selectedSecurity?.categoryCode === SecurityCategory_CategoryCode.INDEX) {
                setSelectedTradeType('strategy')
                return ['strategy']
            }
            else return ['strategy', 'buy', 'sell']
        }
        setSelectedTradeType('buy')
        if (!selectedSecurity?.hasFo) return ['buy', 'sell']
        return ['buy', 'sell']
    }, [selectedSecurity, securityAuto])

    const showLots = selectedTradeType !== 'strategy'

    useEffect(() => {
        if (open && !editTemplateId && quickTradeDialogMode === 'addOrEdit') {
            setNameError('')
            setTemplateError('')
            setQuantityError('')
            setQuickTrade(defaultQuickTrade())
            setSecurityAuto(true)
            resetSlTargetState()
        }
    }, [open, editTemplateId, quickTradeDialogMode, resetSlTargetState])

    const handleClose = useCallback(() => {
        resetSlTargetState()
        dispatch(setOpenQuickTradeSettingsDialog({
            settingsDialogOpen: false,
            dialogMode: 'view',
            editTemplateId: undefined
        }))
    }, [dispatch, resetSlTargetState])

    const onStrategySelected = (template: StrategyTemplateProto) => {
        setTemplateError('')
        setQuickTrade({
            ...quickTrade,
            templateOrderExecutionParameters: {
                ...quickTrade.templateOrderExecutionParameters,
                strategyLegs: template.strategyTemplateLegs
            },
            actionName: template.name,
            description: template.description
        })
    }

    const setStrategyLegs = (legs: StrategyTemplateLegProto[]) => {
        setQuickTrade(quickTrade => ({
            ...quickTrade,
            templateOrderExecutionParameters: {
                ...quickTrade.templateOrderExecutionParameters,
                strategyLegs: {
                    strategyTemplateLegs: legs
                }
            }
        }))
    }

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNameError('')
        setQuickTrade({ ...quickTrade, actionName: e.target.value })
    }

    const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setQuickTrade({ ...quickTrade, description: e.target.value })
    }

    const handleMultiplierChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, quantityType: TemplateQuantityType) => {
        const parsed = parseTemplateQuantityInput(e.target.value, quantityType)
        const data = { ...quickTrade }
        if (quantityType === QuickTradeQuantityType.AMOUNT) {
            data.templateOrderExecutionParameters = {
                ...data.templateOrderExecutionParameters,
                multiplier: undefined,
                lots: undefined,
                quantity: undefined,
                amount: parsed,
            } as any
        } else if (quantityType === QUANTITY_MULTIPLIER) {
            data.templateOrderExecutionParameters = {
                ...quickTrade.templateOrderExecutionParameters,
                multiplier: parsed,
                amount: undefined,
                lots: undefined,
                quantity: undefined,
            } as any
        } else if (quantityType === QuickTradeQuantityType.LOTS) {
            data.templateOrderExecutionParameters = {
                ...quickTrade.templateOrderExecutionParameters,
                multiplier: undefined,
                amount: undefined,
                lots: parsed,
                quantity: undefined,
            } as any
        } else if (quantityType === QuickTradeQuantityType.QUANTITY) {
            data.templateOrderExecutionParameters = {
                ...quickTrade.templateOrderExecutionParameters,
                multiplier: undefined,
                amount: undefined,
                lots: undefined,
                quantity: parsed,
            } as any
        }
        setQuantityError('')
        setQuickTrade(data)
    }

    const handleQuoteTypeChange = useCallback((type: QuoteType) => {
        let quoteValue = quickTrade.templateOrderExecutionParameters?.quoteValue ?? 1
        if (type === QuoteType.LEVEL) {
            quoteValue = 0
        }
        setQuickTrade({
            ...quickTrade,
            templateOrderExecutionParameters: {
                ...quickTrade.templateOrderExecutionParameters,
                quoteType: type,
                quoteValue,
            } as any,
        })
    }, [quickTrade])

    const handleQuoteValueChange = useCallback((value: number) => {
        setQuickTrade({
            ...quickTrade,
            templateOrderExecutionParameters: {
                ...quickTrade.templateOrderExecutionParameters,
                quoteValue: value,
            } as any,
        })
    }, [quickTrade])

    const handleSubmit = () => {
        if (selectedTradeType === 'strategy' && (!quickTrade.templateOrderExecutionParameters?.strategyLegs || quickTrade.templateOrderExecutionParameters.strategyLegs.strategyTemplateLegs.length === 0)) {
            setTemplateError('Please select a strategy template')
            return
        }
        if (!quickTrade.actionName) {
            setNameError('Please set action name')
            return
        }
        if (conditionalEntry.enabled && !isConditionalEntryReady(conditionalEntry)) {
            setTemplateError('Please complete conditional entry rule')
            return
        }
        if (conditionalExit.enabled && !isConditionalEntryReady(conditionalExit)) {
            setTemplateError('Please complete conditional exit rule')
            return
        }
        setTemplateError('')
        const newQuickTrade = { ...quickTrade }
        if (requiresPinnedSymbol) {
            if (!newQuickTrade.security?.code) {
                if (!quantitySecurity?.code) {
                    setTemplateError('Select a symbol for conditions with a fixed value')
                    return
                }
                newQuickTrade.security = chartPrimarySecurity(quantitySecurity)
            }
        } else if (securityAuto) {
            newQuickTrade.security = undefined
        }

        let templateParams = { ...newQuickTrade.templateOrderExecutionParameters }

        if (selectedTradeType !== 'strategy') {
            templateParams = { ...templateParams, multiplier: undefined, strategyLegs: undefined }
            if (quantityType === QuickTradeQuantityType.AMOUNT) {
                templateParams = { ...templateParams, lots: undefined, quantity: undefined }
            } else if (quantityType === QuickTradeQuantityType.LOTS) {
                templateParams = { ...templateParams, amount: undefined, quantity: undefined }
            } else if (quantityType === QuickTradeQuantityType.QUANTITY) {
                templateParams = { ...templateParams, amount: undefined, lots: undefined }
            }

            const brokerOrderType = templateParams.brokerOrderType
            if (
                brokerOrderType === Broker_OrderType.LIMIT_ORDER ||
                brokerOrderType === Broker_OrderType.STOPLOSS_ORDER
            ) {
                if (templateParams.quoteType == null) {
                    setTemplateError('Please set limit price offset type')
                    return
                }
                if (templateParams.quoteType === QuoteType.LEVEL && templateParams.quoteValue == null) {
                    templateParams = { ...templateParams, quoteValue: 0 }
                }
                if (templateParams.quoteType !== QuoteType.LEVEL && templateParams.quoteValue == null) {
                    setTemplateError('Please set limit price offset value')
                    return
                }
            }

            const unitPriceDisplay = computeQuickTradeEntryPriceDisplay({
                quote: readQuickTradeQuoteScalars(quantityQuoteKey),
                orderBook: levelOrderBook,
                transactionType:
                    selectedTradeType === 'buy' ? TransactionType.BUY : TransactionType.SELL,
                quoteType: templateParams.quoteType,
                quoteValue: templateParams.quoteValue,
                tickSize: quantitySecurity?.tickSize ?? 5,
                priceMultiplier: quantitySecurity?.priceMultiplier ?? 100,
            })
            const qtyValidation = validateTemplateQuantityForSave({
                quantityType: quantityType as QuickTradeQuantityType,
                lots: templateParams.lots,
                quantity: templateParams.quantity,
                amount: templateParams.amount,
                multiplier: templateParams.multiplier,
                lotSize: quantitySecurity?.lotSize ?? 1,
                foQuantityMode,
                unitPriceDisplay,
            })
            if (qtyValidation.ok === false) {
                setQuantityError(qtyValidation.message)
                return
            }
            setQuantityError('')

            newQuickTrade.templateOrderExecutionParameters = applyTemplateParamsExtras({
                ...templateParams,
                transactionType: selectedTradeType === 'buy' ? TransactionType.BUY : TransactionType.SELL,
            } as TemplateOrderExecutionParameters)
        } else {
            newQuickTrade.templateOrderExecutionParameters = applyTemplateParamsExtras({
                ...templateParams,
                transactionType: undefined
            } as TemplateOrderExecutionParameters)
        }
        if (mode === 'edit') {
            modifyQuickTrade(newQuickTrade)
        } else {
            createQuickTrade({ body: newQuickTrade })
        }
        handleClose()
        setQuickTrade(defaultQuickTrade())
        setSecurityAuto(true)
        resetSlTargetState()
    }

    const slTargetInputStep = useMemo(() => {
        isStrategyMode ? 1 : (quantitySecurity?.tickSize ?? 100) / (quantitySecurity?.priceMultiplier ?? 100)
    }, [isStrategyMode, quantitySecurity])

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            PaperProps={{ sx: { minWidth: '800px' } }}
            sx={{ zIndex: 13000 }}
        >
            <DraggableDialogTitle id="alert-dialog-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1, pl: quickTradeDialogMode !== 'view' ? 1 : 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {
                        quickTradeDialogMode !== 'view' ?
                            <IconButton
                                aria-label="close"
                                onClick={() => {
                                    dispatch(setOpenQuickTradeSettingsDialog({
                                        settingsDialogOpen: true,
                                        dialogMode: 'view',
                                        editTemplateId: undefined
                                    }))
                                }}
                                sx={{ height: '34px' }}
                            >
                                <ArrowBackIcon fontSize={"small"} />
                            </IconButton>
                            : null
                    }
                    {
                        quickTradeDialogMode === 'view' ?
                            'Quick Trade Settings' :
                            mode === 'edit' ?
                                'Edit Quick Trade' :
                                'Add Quick Trade'
                    }
                </Box>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        height: '34px'
                    }}
                >
                    <ClearIcon fontSize={"small"} />
                </IconButton>
            </DraggableDialogTitle>
            <DialogContent sx={quickTradeDialogMode === 'view' ? { p: 0 } : { pb: 0, maxHeight: 'calc(100vh - 120px)', overflow: 'auto' }}>
                {
                    quickTradeDialogMode === 'view' ?
                        <Box sx={{ height: '100%' }}>
                            {
                                quickTrades.length === 0 ?
                                    null :
                                    <Table sx={{ tableLayout: "auto", width: "100%" }}>
                                        <TableHead sx={{ background: theme => theme.palette.background.default }}>
                                            <TableRow sx={{ height: '34px' }}>
                                                <TableCell sx={{ py: 0 }}>Action Name</TableCell>
                                                <TableCell sx={{ py: 0 }}>Template</TableCell>
                                                <TableCell sx={{ py: 0 }}>Description</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {
                                                quickTrades.map((q) => (
                                                    <TableRow
                                                        sx={{
                                                            height: '34px',
                                                            position: 'relative',
                                                            '&:hover': {
                                                                background: theme => theme.palette.action.hover
                                                            },
                                                            '&:hover button': {
                                                                visibility: 'visible !important',
                                                                background: theme => theme.palette.background.default
                                                            }
                                                        }}
                                                    >
                                                        <TableCell sx={{ py: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={q.actionName}>{q.actionName}</TableCell>
                                                        <TableCell sx={{ py: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}><QuickTradeTooltip quickTrade={q} dontShowStrategyLegs /></TableCell>
                                                        <TableCell sx={{ py: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={q.description ? q.description : '-'}>{q.description ? q.description : '-'}</TableCell>
                                                        <Stack direction="row" sx={{ position: 'sticky', right: 0, top: 0, height: '32px' }}>
                                                            <NonInteractiveTooltip arrow title="Delete" placement='top'>
                                                                <IconButton
                                                                    sx={{ width: '32px', height: '32px', visibility: 'hidden' }}
                                                                    onClick={() => {
                                                                        deleteQuickTrade(q)
                                                                    }}
                                                                >
                                                                    <BinIcon fontSize="small" />
                                                                </IconButton>
                                                            </NonInteractiveTooltip>
                                                            <NonInteractiveTooltip arrow title="Edit" placement='top'>
                                                                <IconButton
                                                                    sx={{ width: '32px', height: '32px', visibility: 'hidden' }}
                                                                    onClick={() => {
                                                                        dispatch(setOpenQuickTradeSettingsDialog({
                                                                            settingsDialogOpen: true,
                                                                            dialogMode: 'addOrEdit',
                                                                            editTemplateId: q.id
                                                                        }))
                                                                    }}
                                                                >
                                                                    <Edit />
                                                                </IconButton>
                                                            </NonInteractiveTooltip>
                                                        </Stack>
                                                    </TableRow>
                                                ))
                                            }
                                        </TableBody>
                                    </Table>
                            }
                            <ExtraSmallButton variant="outlined" sx={{ pr: 1, ml: 2, position: 'sticky', left: '16px', mt: quickTrades.length > 0 ? 1 : 0 }} onClick={() => {
                                dispatch(setOpenQuickTradeSettingsDialog({
                                    settingsDialogOpen: true,
                                    dialogMode: 'addOrEdit',
                                    editTemplateId: undefined
                                }))
                            }}>
                                <AddIcon />
                                {' Add new action'}
                            </ExtraSmallButton>
                        </Box> :
                        <Grid container direction={'column'} rowSpacing={2}>
                            <Grid container item spacing={2}>
                                <Grid item container direction={'column'} alignItems={'flex-start'} xs={4}>
                                    <Stack direction={'row'} sx={{ width: '100%', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" sx={{ display: 'flex', ...aboveFieldLabelSx }}>
                                            Symbol
                                            <InteractiveTooltip title="Price Offset on the current market price" placement='top' arrow>
                                                <IconButton size="small" sx={{ ml: '2px', p: 0 }}>
                                                    <Info sx={{ fontSize: '14px' }} />
                                                </IconButton>
                                            </InteractiveTooltip>
                                        </Typography>
                                    </Stack>
                                    <Button
                                        disableRipple
                                        color="inherit"
                                        variant='outlined'
                                        onClick={() => {
                                            setSecuritiesModalOpen(true)
                                        }}
                                        sx={{
                                            borderRadius: '4px',
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            '& .MuiTypography-root': {
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            },
                                            minWidth: 'max-content !important',
                                            width: '100%',
                                            px: 1,
                                        }}
                                        data-event={'Search Clicked'}
                                        data-event-source={'stockbar'}
                                    >
                                        <Stack direction={'row'} spacing={1} sx={{ width: '100%' }} alignItems={'center'} justifyContent={'space-between'}>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                {securityAuto ? "Pick From Chart" :
                                                    selectedSecurity?.logoUrl ? (
                                                        <Avatar alt={selectedSecurity?.displayName} src={`${LOGOS_BASE_URL}${selectedSecurity?.logoUrl}.svg`} sx={{ width: 20, height: 20 }} />
                                                    ) : (
                                                        <Avatar
                                                            alt={selectedSecurity?.displayName}
                                                            sx={{
                                                                width: 20,
                                                                height: 20,
                                                                fontSize: 10,
                                                                color: 'white',
                                                                bgcolor: getColorForSymbol(selectedSecurity?.symbol || selectedSecurity?.code || '', theme.palette.background.default)
                                                            }}
                                                        >
                                                            {(selectedSecurity?.symbol || selectedSecurity?.code || '')
                                                                .split(' ')
                                                                .map((word) => word[0]?.toUpperCase())
                                                                .slice(0, 2)
                                                                .join('')}
                                                        </Avatar>
                                                    )}
                                                {
                                                    securityAuto ? null :
                                                        <Typography noWrap variant="body1" maxWidth={'120px'} sx={{ fontWeight: 500 }}>
                                                            {selectedSecurity?.symbol}
                                                        </Typography>
                                                }
                                            </span>
                                            {
                                                securityAuto || requiresPinnedSymbol ? null :
                                                    <NonInteractiveTooltip title="Clear symbol" placement="top" arrow>
                                                        <ExtraSmallButtonHoverable
                                                            color="inherit"
                                                            aria-label="Clear symbol"
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                setSecurityAuto(true)
                                                                setQuickTrade((q) => ({ ...q, security: undefined }))
                                                            }}
                                                            sx={{ minWidth: 28, width: 28, height: 28, p: 0 }}
                                                        >
                                                            <ClearIcon sx={{ fontSize: 16 }} />
                                                        </ExtraSmallButtonHoverable>
                                                    </NonInteractiveTooltip>
                                            }
                                        </Stack>
                                    </Button>
                                </Grid>
                                <Grid item container direction={'column'} alignItems={'flex-start'} xs={4}>
                                    <Typography variant="caption" sx={aboveFieldLabelSx}>Trade Type</Typography>
                                    <ToggleButtonGroup value={selectedTradeType} onChange={(e, value) => {
                                        if (value !== null) {
                                            setTemplateError('')
                                            setQuantityError('')
                                            setSelectedTradeType(value)
                                        }
                                    }} exclusive sx={{ height: '34px', width: '100%' }}>
                                        <ToggleButton sx={{ flex: 1 }} value='buy' disabled={!showTradeOptions.includes('buy')}>Buy</ToggleButton>
                                        <ToggleButton sx={{ flex: 1 }} value='sell' disabled={!showTradeOptions.includes('sell')}>Sell</ToggleButton>
                                        <ToggleButton sx={{ flex: 1 }} value='strategy' disabled={!showTradeOptions.includes('strategy')}>Strategy</ToggleButton>
                                    </ToggleButtonGroup>
                                </Grid>
                                <Grid item container direction={'column'} alignItems={'flex-start'} xs={4}>
                                    <Typography variant="caption" sx={aboveFieldLabelSx}>Template</Typography>
                                    <StrategiesMenu
                                        onStrategyClicked={onStrategySelected}
                                        showAddTemplate
                                        disabled={selectedTradeType !== 'strategy'}
                                        sxProps={{
                                            width: '100%'
                                        }}
                                        error={Boolean(templateError)}
                                        errorMessage={templateError}
                                    />
                                </Grid>
                                {(securityModalOpen) && (
                                    <SecuritySearchModal
                                        open={securityModalOpen}
                                        deleteIcon={true}
                                        enableExpressions={false}
                                        onCloseClicked={() => {
                                            setSecuritiesModalOpen(false)
                                        }}
                                        onSecuritySelected={(data: ExpressionCallbackData) => {
                                            const [security] = data.securities
                                            setQuickTrade({
                                                ...quickTrade,
                                                security: {
                                                    code: security?.code,
                                                    exchange: security?.exchange
                                                }
                                            })
                                            setSecurityAuto(false)
                                            setSecuritiesModalOpen(false)
                                        }}
                                    />
                                )}
                            </Grid>
                            {
                                (selectedTradeType !== 'strategy') ? null :
                                    <Grid container item direction={'column'} gap={2}>
                                        <StrategyLegsRenderer
                                            strategyLegs={quickTrade?.templateOrderExecutionParameters?.strategyLegs?.strategyTemplateLegs ?? []}
                                            setStrategyLegs={setStrategyLegs as any}
                                        />
                                    </Grid>
                            }
                            <Grid container item spacing={2}>
                                <Grid item container direction={'column'} alignItems={'flex-start'} xs={4}>
                                    <Typography variant="caption" sx={aboveFieldLabelSx}>Order Type</Typography>
                                    <Select value={quickTrade.templateOrderExecutionParameters?.brokerOrderType} MenuProps={{ sx: { zIndex: 13000 } }} sx={{ width: '100%', height: '34px' }} onChange={(e) => {
                                        const newData = {
                                            ...quickTrade, templateOrderExecutionParameters: {
                                                ...quickTrade.templateOrderExecutionParameters,
                                                brokerOrderType: e.target.value as Broker_OrderType
                                            }
                                        }
                                        if (e.target.value === Broker_OrderType.MARKET_ORDER) {
                                            delete newData.templateOrderExecutionParameters.quoteType
                                            delete newData.templateOrderExecutionParameters.quoteValue
                                        } else {
                                            newData.templateOrderExecutionParameters.quoteType = QuoteType.BIPS
                                            newData.templateOrderExecutionParameters.quoteValue = 1
                                        }
                                        setQuickTrade(newData)
                                    }}>
                                        <MenuItem value={Broker_OrderType.MARKET_ORDER}>Market Order</MenuItem>
                                        <MenuItem value={Broker_OrderType.LIMIT_ORDER}>Limit Order</MenuItem>
                                    </Select>
                                </Grid>
                                <Grid item container direction={'column'} alignItems={'flex-start'} xs={4}>
                                    <QuickTradePriceOffsetFieldWithLiveQuote
                                        liveQuoteSecurityKey={quantityQuoteKey}
                                        disabled={
                                            quickTrade.templateOrderExecutionParameters?.brokerOrderType !==
                                            Broker_OrderType.LIMIT_ORDER
                                        }
                                        quoteType={
                                            quickTrade.templateOrderExecutionParameters?.quoteType ?? QuoteType.BIPS
                                        }
                                        quoteValue={quickTrade.templateOrderExecutionParameters?.quoteValue}
                                        transactionType={quantityTransactionType}
                                        tickSize={quantitySecurity?.tickSize ?? 5}
                                        priceMultiplier={quantitySecurity?.priceMultiplier ?? 100}
                                        onQuoteTypeChange={handleQuoteTypeChange}
                                        onQuoteValueChange={handleQuoteValueChange}
                                        labelAdornment={
                                            <InteractiveTooltip title="Offset on market price" placement="top" arrow>
                                                <IconButton size="small" sx={{ ml: '2px', p: 0 }}>
                                                    <Info sx={{ fontSize: '14px' }} />
                                                </IconButton>
                                            </InteractiveTooltip>
                                        }
                                    />
                                </Grid>
                                <Grid item container direction={'column'} alignItems={'flex-start'} xs={4}>
                                    {isStrategyMode ? (
                                        <>
                                            <Typography variant="caption" sx={aboveFieldLabelSx}>
                                                Qty Multiplier
                                            </Typography>
                                            <TextField
                                                value={quickTrade.templateOrderExecutionParameters?.multiplier}
                                                type="number"
                                                onChange={(e) => handleMultiplierChange(e, QUANTITY_MULTIPLIER)}
                                                error={!!quantityError}
                                                helperText={quantityError || undefined}
                                                sx={{
                                                    width: '100%',
                                                    '& .MuiFormHelperText-root': { margin: 0 },
                                                    '& .MuiInputBase-root': {
                                                        height: '34px',
                                                    },
                                                }}
                                            />
                                        </>
                                    ) : (
                                        <QuickTradeQuantityFieldWithLiveQuote
                                            liveQuoteSecurityKey={quantityQuoteKey}
                                            quantityType={quantityType as QuickTradeQuantityType}
                                            value={quantityFieldValue}
                                            onQuantityTypeChange={(type) => {
                                                setQuantityType(type)
                                                handleMultiplierChange({ target: { value: 1 } } as any, type)
                                            }}
                                            onValueChange={(e) => handleMultiplierChange(e, quantityType)}
                                            showLots={showLots}
                                            showQuantity={showQuantity}
                                            showAmount={showAmount}
                                            foQuantityMode={foQuantityMode}
                                            lotSize={quantitySecurity?.lotSize ?? 1}
                                            priceMultiplier={quantitySecurity?.priceMultiplier ?? 100}
                                            tickSize={quantitySecurity?.tickSize ?? 5}
                                            quoteType={quickTrade.templateOrderExecutionParameters?.quoteType}
                                            quoteValue={quickTrade.templateOrderExecutionParameters?.quoteValue}
                                            transactionType={quantityTransactionType}
                                            error={!!quantityError}
                                            helperText={quantityError || undefined}
                                        />
                                    )}
                                </Grid>
                            </Grid>
                            <Grid container item spacing={2}>
                                <Grid item container direction={'column'} alignItems={'flex-start'} xs={6} sx={aboveFieldColumnSx}>
                                    <TextField
                                        label="Stop loss"
                                        InputLabelProps={{ shrink: true }}
                                        type="number"
                                        size="small"
                                        fullWidth
                                        value={stopLoss}
                                        autoComplete="off"
                                        onChange={(e) => {
                                            const raw = e.target.value
                                            if (raw === '') {
                                                setStopLoss('')
                                                setAddStopLoss(false)
                                                return
                                            }
                                            const num = Number(raw)
                                            if (!Number.isNaN(num) && num >= 0) {
                                                setStopLoss(num)
                                                setAddStopLoss(true)
                                            }
                                        }}
                                        inputProps={{ step: slTargetInputStep, min: 0 }}
                                        InputProps={{
                                            sx: {
                                                '& input[type=number]': { MozAppearance: 'textfield' },
                                                paddingRight: 0.5,
                                            },
                                            endAdornment: (
                                                <>
                                                    {stopLoss !== '' ? (
                                                        <IconButton
                                                            size="small"
                                                            aria-label="Clear stop loss"
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                setStopLoss('')
                                                                setAddStopLoss(false)
                                                            }}
                                                            sx={slTargetClearBtnSx}
                                                        >
                                                            <SlTargetClearIcon sx={{ fontSize: 14 }} />
                                                        </IconButton>
                                                    ) : null}
                                                    <ToggleButtonGroup
                                                        size="small"
                                                        value={isStrategyMode ? 'mtm' : stopLossType}
                                                        sx={{
                                                            height: '28px',
                                                            '.MuiButtonBase-root': {
                                                                borderColor: (t) => t.palette.background.default,
                                                            },
                                                        }}
                                                    >
                                                        {isStrategyMode ? (
                                                            <ToggleButton value="mtm" selected disabled sx={{ height: '28px' }}>
                                                                MTM
                                                            </ToggleButton>
                                                        ) : (
                                                            <>
                                                                <ToggleButton
                                                                    value="points"
                                                                    selected={stopLossType === slTargetType.POINTS}
                                                                    onClick={() => setStopLossType(slTargetType.POINTS)}
                                                                    sx={{ height: '28px' }}
                                                                >
                                                                    Pts
                                                                </ToggleButton>
                                                                <ToggleButton
                                                                    value="percentage"
                                                                    selected={stopLossType === slTargetType.PERCENTAGE}
                                                                    onClick={() => setStopLossType(slTargetType.PERCENTAGE)}
                                                                    sx={{ height: '28px' }}
                                                                >
                                                                    %
                                                                </ToggleButton>
                                                            </>
                                                        )}
                                                    </ToggleButtonGroup>
                                                </>
                                            ),
                                        }}
                                        sx={{ '& .MuiInputBase-root': { height: '34px' }, width: '100%' }}
                                    />
                                </Grid>
                                <Grid item container direction={'column'} alignItems={'flex-start'} xs={6} sx={aboveFieldColumnSx}>
                                    <TextField
                                        label="Target"
                                        InputLabelProps={{ shrink: true }}
                                        type="number"
                                        size="small"
                                        fullWidth
                                        value={target}
                                        autoComplete="off"
                                        onChange={(e) => {
                                            const raw = e.target.value
                                            if (raw === '') {
                                                setTarget('')
                                                setAddTarget(false)
                                                return
                                            }
                                            const num = Number(raw)
                                            if (!Number.isNaN(num) && num >= 0) {
                                                setTarget(num)
                                                setAddTarget(true)
                                            }
                                        }}
                                        inputProps={{ step: slTargetInputStep, min: 0 }}
                                        InputProps={{
                                            sx: {
                                                '& input[type=number]': { MozAppearance: 'textfield' },
                                                paddingRight: 0.5,
                                            },
                                            endAdornment: (
                                                <>
                                                    {target !== '' ? (
                                                        <IconButton
                                                            size="small"
                                                            aria-label="Clear target"
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                setTarget('')
                                                                setAddTarget(false)
                                                            }}
                                                            sx={slTargetClearBtnSx}
                                                        >
                                                            <SlTargetClearIcon sx={{ fontSize: 14 }} />
                                                        </IconButton>
                                                    ) : null}
                                                    <ToggleButtonGroup
                                                        size="small"
                                                        value={isStrategyMode ? 'mtm' : targetType}
                                                        sx={{
                                                            height: '28px',
                                                            '.MuiButtonBase-root': {
                                                                borderColor: (t) => t.palette.background.default,
                                                            },
                                                        }}
                                                    >
                                                        {isStrategyMode ? (
                                                            <ToggleButton value="mtm" selected disabled sx={{ height: '28px' }}>
                                                                MTM
                                                            </ToggleButton>
                                                        ) : (
                                                            <>
                                                                <ToggleButton
                                                                    value="points"
                                                                    selected={targetType === slTargetType.POINTS}
                                                                    onClick={() => setTargetType(slTargetType.POINTS)}
                                                                    sx={{ height: '28px' }}
                                                                >
                                                                    Pts
                                                                </ToggleButton>
                                                                <ToggleButton
                                                                    value="percentage"
                                                                    selected={targetType === slTargetType.PERCENTAGE}
                                                                    onClick={() => setTargetType(slTargetType.PERCENTAGE)}
                                                                    sx={{ height: '28px' }}
                                                                >
                                                                    %
                                                                </ToggleButton>
                                                            </>
                                                        )}
                                                    </ToggleButtonGroup>
                                                </>
                                            ),
                                        }}
                                        sx={{ '& .MuiInputBase-root': { height: '34px' }, width: '100%' }}
                                    />
                                </Grid>
                            </Grid>
                            {quantitySecurity && quantityQuoteKey ? (
                                <Grid container item spacing={2}>
                                    <Grid item container direction="column" alignItems="flex-start" xs={6} sx={aboveFieldColumnSx}>
                                        <QuickTradeConditionalEntryWithLiveQuote
                                            variant="entry"
                                            security={quantitySecurity}
                                            displaySymbol={conditionalDisplaySymbol}
                                            liveQuoteSecurityKey={quantityQuoteKey}
                                            useZeroQuote={!!basketConditionalModalVariants}
                                            priceMultiplier={quantitySecurity.priceMultiplier ?? 100}
                                            disabled={false}
                                            allowEnableConditionalEntry
                                            value={conditionalEntry}
                                            onChange={handleConditionalEntryChange}
                                            compact
                                            tradePanelAdvanced
                                            conditionalModalChartVariants={basketConditionalModalVariants}
                                        />
                                    </Grid>
                                    <Grid item container direction="column" alignItems="flex-start" xs={6} sx={aboveFieldColumnSx}>
                                        <QuickTradeConditionalEntryWithLiveQuote
                                            variant="exit"
                                            security={quantitySecurity}
                                            displaySymbol={conditionalDisplaySymbol}
                                            liveQuoteSecurityKey={quantityQuoteKey}
                                            useZeroQuote={!!basketConditionalModalVariants}
                                            priceMultiplier={quantitySecurity.priceMultiplier ?? 100}
                                            disabled={false}
                                            allowEnableConditionalEntry
                                            value={conditionalExit}
                                            onChange={handleConditionalExitChange}
                                            compact
                                            tradePanelAdvanced
                                            conditionalModalChartVariants={basketConditionalModalVariants}
                                        />
                                    </Grid>
                                </Grid>
                            ) : null}
                            <Grid container item spacing={2}>
                                <Grid item xs={4}>
                                    <TextField
                                        label="Action name"
                                        required
                                        InputLabelProps={{ shrink: true }}
                                        size="small"
                                        fullWidth
                                        sx={{
                                            '& .MuiFormHelperText-root': {
                                                margin: 0
                                            },
                                            '& .MuiInputBase-root': {
                                                height: '34px'
                                            },
                                        }}
                                        error={!!nameError}
                                        helperText={nameError ? nameError : undefined}
                                        value={quickTrade.actionName}
                                        onChange={handleNameChange}
                                    />
                                </Grid>
                                <Grid item xs={8}>
                                    <TextField
                                        label="Description"
                                        InputLabelProps={{ shrink: true }}
                                        size="small"
                                        fullWidth
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                height: '34px'
                                            }
                                        }}
                                        value={quickTrade.description}
                                        onChange={handleDescriptionChange}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                }
            </DialogContent>
            <DialogActions sx={{ pt: 1 }}>
                {
                    quickTradeDialogMode === 'view' ? null :
                        <>
                            <ExtraSmallButton variant="outlined" onClick={() => {
                                setQuickTrade(defaultQuickTrade())
                                setSecurityAuto(true)
                                handleClose()
                            }}>
                                Cancel
                            </ExtraSmallButton>
                            <ExtraSmallButton variant="contained" sx={{ width: mode === 'edit' ? '70px' : '50px' }} onClick={handleSubmit}>
                                {
                                    mode === 'edit' ? 'Update' : 'Add'
                                }
                            </ExtraSmallButton>
                        </>
                }
            </DialogActions>
        </Dialog>
    )
}

export default AddQuickTrade